import { BCDataGrid, BCExtremeGridEntities, BCLoading, findMessage } from '@bimser/components';
import { isNullOrUndefined } from '@bimser/core';
import * as React from 'react';
import { DMListView, DmObjectType, IDmData } from '../../..';
import { INavigationBox } from '../../DMListView/entities/IDMGridProps';
import Styles from '../assets/styles.scss';
import { DMExplorerProps, INavigationHistoryItem, INavigationItem } from '../entities';

const DMExplorer = (props: DMExplorerProps) => {
	const bcDataGridRef = React.useRef<BCDataGrid.Entities.BCDataGridRef>();
	const folderContextItems = React.useRef<DMListView.IGridContextItem[]>([
		{
			caption: findMessage.get('100021'),
			dataType: 'string',
			displayFormat: '',
			name: 'NAME',
			order: 1
		},
		{
			caption: findMessage.get('100022'),
			dataType: 'string',
			displayFormat: '',
			name: 'DESCRIPTION',
			order: 2
		}
	]);

	const [navigationItems, setNavigationItems] = React.useState<INavigationHistoryItem[]>([
		props.currentItem
	]);
	const [currentItem, setCurrentItem] = React.useState<INavigationHistoryItem>(props.currentItem);
	const [initialLoading, setInitialLoading] = React.useState<boolean>(true);

	React.useEffect(() => {
		if (!initialLoading) {
			bcDataGridRef.current.instance().repaint();
		}
	}, [initialLoading]);

	let allowMultipleSelection = false;
	if (isNullOrUndefined(props.maxFileCount)) {
		allowMultipleSelection = !!props.allowMultipleSelection;
	} else if (!isNullOrUndefined(props.maxFileCount) && props.maxFileCount > 1) {
		allowMultipleSelection = true;
	}

	const selectionOptions: DMListView.ISelectionOptions = {
		enable: true,
		allowMultipleSelection,
		selectionLimit: props.maxFileCount || 1,
		restrict: props.extensions?.length > 0,
		selectionType:
			props.extensions?.length > 0
				? [DmObjectType.File]
				: [DmObjectType.Repository, DmObjectType.Folder]
	};

	const onDblClick = React.useCallback(
		(...[arg]: Parameters<BCDataGrid.Entities.BCDataGridProps['onRowDblClick']>) => {
			const newItem = arg.data.baseItem.toJS();

			if (newItem.type !== 2) {
				// Folders
				addNewNavigationItem(newItem, currentItem.id);
				setCurrentItem(newItem);
			}
		},
		[selectionOptions]
	);

	const onSelectionChange = React.useCallback(
		(items: Array<IDmData['metadata']>) => {
			const selectedItems: INavigationItem[] = items
				.filter(item => {
					if (props.extensions?.length) {
						// Only files
						return item.TYPE === 2;
					} else {
						// Only folders
						return item.TYPE === 0 || item.TYPE === 1;
					}
				})
				.map(item => {
					return {
						id: item.ID,
						name: item.NAME,
						secretKey: item.SECRETKEY,
						type: item.TYPE,
						extension: null,
						baseItem: item.baseItem?.toJS()
					};
				});

			props.onSelectionChange(selectedItems);
		},
		[selectionOptions]
	);

	const isLastNavigationItemSelected = () => {
		return currentItem.id === navigationItems[navigationItems.length - 1].id;
	};

	const addNewNavigationItem = (item: INavigationHistoryItem, currentItemId: string) => {
		const itemAlreadyExist = navigationItems.findIndex(i => i.id === item.id) > -1;

		if (itemAlreadyExist) return;

		if (isLastNavigationItemSelected()) {
			setNavigationItems([...navigationItems, item]);
		} else {
			const currentIndex = navigationItems.findIndex(i => i.id === currentItemId);
			const newItems = navigationItems.slice(0, currentIndex + 1);
			setNavigationItems([...newItems, item]);
		}
	};

	const goBack = () => {
		const currentIndex = navigationItems.findIndex(i => i.id === currentItem.id);
		const prevItem = navigationItems[currentIndex - 1];
		if (prevItem) {
			setCurrentItem(navigationItems[currentIndex - 1]);
		}
	};

	const goForward = () => {
		const currentIndex = navigationItems.findIndex(i => i.id === currentItem.id);
		const nextItem = navigationItems[currentIndex + 1];
		if (nextItem) {
			setCurrentItem(navigationItems[currentIndex + 1]);
		}
	};

	const onNavigationItemChanged = (value: string) => {
		const item = navigationItems.find(i => i.id === value);
		setCurrentItem(item);
	};

	const navigationBox = React.useMemo<INavigationBox>(
		() => ({
			visible: true,
			items: navigationItems,
			onValueChanged: onNavigationItemChanged,
			value: currentItem.id,
			goBack,
			goForward
		}),
		[navigationItems, currentItem.id]
	);

	const getFilters = () => {
		if (props.extensions?.includes('*')) return [];

		if (props.extensions?.length) {
			// Folder or files ends with extension

			const extensionFilters = props.extensions.map(extension => {
				return {
					Column: 'NAME',
					Not: false,
					Type: 8,
					Value: `.${extension}`,
					ItemType: 1
				};
			});

			return [
				{
					Operator: 2,
					Filters: [
						// Folders
						{
							Column: 'type',
							ItemType: BCExtremeGridEntities.ItemType.Int32,
							Not: false,
							Type: BCExtremeGridEntities.FilterType.Equal,
							Value: 1
						},
						{
							Operator: 1,
							Filters: [
								// File and ends with extension
								{
									Column: 'type',
									ItemType: BCExtremeGridEntities.ItemType.Int32,
									Not: false,
									Type: BCExtremeGridEntities.FilterType.Equal,
									Value: 2
								},
								{
									Operator: 2,
									Filters: extensionFilters
								}
							]
						}
					]
				}
			];
		}

		return [
			// Only Folders
			{
				Column: 'type',
				ItemType: BCExtremeGridEntities.ItemType.Int32,
				Not: false,
				Type: BCExtremeGridEntities.FilterType.Equal,
				Value: 1
			}
		];
	};

	const onContentReady = React.useCallback(
		(e: BCDataGrid.Entities.DataGridTypes.ContentReadyEvent<any, any>) => {
			if (initialLoading) {
				bcDataGridRef.current = e.component;
				setInitialLoading(false);
			}
		},
		[]
	);

	const renderGrid = () => {
		if (currentItem.id === '#Root') {
			return (
				<DMListView.DMRepositoriesGrid
					sceneId="#Root"
					selectionOptions={selectionOptions}
					onSelectionChange={onSelectionChange}
					onDblClick={onDblClick}
					stateStoringEnabled={false}
					onlyBaseColumns
					navigationBox={navigationBox}
					showRefreshButton
					onContentReady={onContentReady}
				/>
			);
		} else {
			return (
				<DMListView.DMExplorerGrid
					sceneId={currentItem.id.toString()}
					sceneSecretKey={currentItem.secretKey}
					selectionOptions={selectionOptions}
					onSelectionChange={onSelectionChange}
					onDblClick={onDblClick}
					gridContextItems={folderContextItems.current}
					stateStoringEnabled={false}
					navigationBox={navigationBox}
					showRefreshButton
					filters={getFilters()}
					onContentReady={onContentReady}
				/>
			);
		}
	};

	return (
		<div className={Styles.gridContainer}>
			<BCLoading show={initialLoading}>{renderGrid()}</BCLoading>
		</div>
	);
};

export default DMExplorer;
